import { createContext, ReactNode, use, useMemo, useRef } from 'react';
import {
  ClientOrg,
  CustomMusic,
  Hymn,
  HymnalId,
  HymnalManifest,
  HymnalSlug,
  HymnalsManifest,
  OrgHymnal,
  OrgId,
  SongManifestEntry,
} from '../../../common/src/model.ts';
import {
  getHymnalAlternateTitleIconPath,
  getHymnalIconPath,
  getHymnalManifestUrl,
  getSongSlug,
  hymnalsManifestURL,
  orgHymnalDefaultIconPath,
  PrimaryOrgHymnalName,
} from '../../../common/src/paths.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { naturalStringCompare } from '../../../common/src/util.ts';
import { getHymnFromSlug, getUrlPrefixForHymn, PrimaryOrgHymnalId } from '../util/path.ts';
import { DownloadsCompleteFunction } from '../util/use_downloads.tsx';
import { withWebLock } from '../util/web_locks.ts';
import { DownloadFunction } from './client_cache_storage.ts';
import { localStorageGet, LocalStorageKey, localStorageRemove, localStorageSet } from './client_local_storage.ts';
import { reconcileHymnals } from './hymn_identity.ts';
import { getCanonicalHymnal, getDisplayHymnal, HymnalWithHymns } from './hymnals.ts';
import { DownloadQueueAction } from './idb.ts';
import { useCurrentOrg } from './use_current_org.tsx';
import { useCustomMusic } from './use_custom_music.tsx';
import { ContentKind, NetworkResult, useFetch, useFetchArray } from './use_fetch.tsx';
import { useLocalStorage } from './use_local_storage.tsx';

export const PrimaryOrgHymnalSlug = 'PRIMARY';

const orgHymnalRefreshLockName = 'syp-org-hymnal-refresh';

export const FavoritesHymnalId = 0;

export const HymnalsWithIconBorder = ['Tanglewood Hymnal'];
const OrgHymnalIconThresholdPercentage = 75;

export interface HymnalIcon {
  path: string;
  hasBorder?: boolean;
}

export const HymnalsContext = createContext<HymnalWithHymns[]>([]);

// NOTES:
// - Called twice in development whenever any inputs change (purposely in React - only once in production)
// - Called a minimum of 6x (3 pairs) during boot -> initially, when manifest loads, when hymnals populated
// - Not called in steady state during page transitions

export const HymnalsProvider = ({ children }: { children: ReactNode }) => {
  const { customMusic } = useCustomMusic();
  const { content: hymnalsManifest } = useFetch<HymnalsManifest>({
    url: hymnalsManifestURL,
    contentKind: ContentKind.JSON,
  });
  const hymnalUrls = useMemo(
    () =>
      hymnalsManifest === undefined
        ? []
        : Object.keys(hymnalsManifest).map((hymnalName) => getHymnalManifestUrl(hymnalName)),
    [hymnalsManifest],
  );
  const { results: hymnalManifestResults, fetchWorkers } = useFetchArray<HymnalManifest>(hymnalUrls, ContentKind.JSON);
  // The last list we produced, so a manifest refresh that changed one song leaves every other
  // hymn and hymnal referentially identical (issue #3517 item A3). Rebuilding them all handed
  // React new objects for every song, re-running effects keyed on a hymn - notably the current
  // song's notation fetch, which reset its error state.
  const populatedHymnalsRef = useRef<HymnalWithHymns[] | undefined>(undefined);
  // when all not loading
  const populatedHymnals = useMemo(() => {
    if (hymnalsManifest === undefined) {
      return undefined;
    }
    const allHymnalManifestsFetched = hymnalManifestResults.every(
      ({ result, content }) => (result === NetworkResult.Success && !!content) || result === NetworkResult.Failure,
    );
    if (!allHymnalManifestsFetched) {
      return undefined;
    }
    const hymnals = reconcileHymnals(
      populatedHymnalsRef.current,
      getHymnalsFromHymnalManifests(
        hymnalsManifest,
        hymnalManifestResults.map(({ content }) => content).filter((content) => !!content),
      ),
    );
    populatedHymnalsRef.current = hymnals;
    return hymnals;
  }, [hymnalManifestResults, hymnalsManifest]);
  const { currentOrg } = useCurrentOrg();
  const [orgHymnal] = useLocalStorage(LocalStorageKey.OrgHymnal);
  // Likewise for the org hymnal composite, so writing the org hymnal to local storage or a
  // refresh of an unrelated hymnal does not churn its hymns' identities. Its hymns are copies of
  // the canonical hymnal's hymns in the single-primary-hymnal case, so the previous composite is
  // only reused while that case is unchanged - otherwise the copies would be reconciled back into
  // the canonical objects they exist to be distinguished from.
  const combinedOrgHymnalRef = useRef<{ hymnal: HymnalWithHymns; singlePrimaryHymnalId?: HymnalId } | undefined>(
    undefined,
  );
  const combinedOrgHymnal = useMemo(() => {
    if (!currentOrg || !populatedHymnals || populatedHymnals.length === 0) {
      return;
    }
    const singlePrimaryHymnalId = getSinglePrimaryHymnalId(currentOrg);
    const previous = combinedOrgHymnalRef.current;
    const previousHymnal =
      previous && previous.singlePrimaryHymnalId === singlePrimaryHymnalId ? previous.hymnal : undefined;
    const built = getCombinedOrgHymnal(currentOrg, orgHymnal, populatedHymnals, customMusic);
    const [hymnal = built] = reconcileHymnals(previousHymnal ? [previousHymnal] : undefined, [built]);
    combinedOrgHymnalRef.current = { hymnal, singlePrimaryHymnalId };
    return hymnal;
  }, [currentOrg, customMusic, orgHymnal, populatedHymnals]);
  const hymnals = useMemo(
    () =>
      prioritizeOrgHymnals(currentOrg, [
        ...(combinedOrgHymnal ? [combinedOrgHymnal] : []),
        ...(populatedHymnals ?? []),
      ]),
    [combinedOrgHymnal, currentOrg, populatedHymnals],
  );
  return (
    <HymnalsContext value={hymnals}>
      {children}
      {fetchWorkers}
    </HymnalsContext>
  );
};

/********************************** hymnals **********************************/

function prioritizeOrgHymnals(org: ClientOrg | undefined, hymnals: HymnalWithHymns[]) {
  const orgHymnalIds = new Set<HymnalId>([
    ...(org?.hymnals?.map((hymnal) => hymnal.id) ?? []),
    ...(org?.primaryHymnalIds ?? []),
  ]);
  if (orgHymnalIds.size === 0 || hymnals.length === 0) {
    return hymnals;
  }
  const orgHymnal = hymnals.find((hymnal) => hymnal.id === PrimaryOrgHymnalId);
  const primaryHymnalIds = org?.primaryHymnalIds;
  const primaryHymnals = primaryHymnalIds && hymnals.filter((hymnal) => primaryHymnalIds.includes(hymnal.id));
  const nonPrimaryHymnals = hymnals.filter(
    (hymnal) => hymnal.id !== PrimaryOrgHymnalId && !primaryHymnalIds?.includes(hymnal.id),
  );
  const secondaryOrgHymnals = nonPrimaryHymnals.filter((hymnal) => orgHymnalIds.has(hymnal.id));
  const otherHymnals = nonPrimaryHymnals.filter((hymnal) => !orgHymnalIds.has(hymnal.id));
  return [
    ...(orgHymnal ? [orgHymnal] : []),
    ...(primaryHymnals ? primaryHymnals : []),
    ...secondaryOrgHymnals,
    ...otherHymnals,
  ];
}

// The org hymnal's hymns are copies of the primary hymnal's hymns only when the org has exactly
// one primary hymnal
function getSinglePrimaryHymnalId(org: ClientOrg | undefined): HymnalId | undefined {
  return org?.primaryHymnalIds?.length === 1 ? org.primaryHymnalIds[0] : undefined;
}

function getCombinedOrgHymnal(
  org: ClientOrg,
  orgHymnal: OrgHymnal,
  hymnals: HymnalWithHymns[],
  customMusic: CustomMusic,
): HymnalWithHymns {
  const singlePrimaryHymnalId = getSinglePrimaryHymnalId(org);
  const singlePrimaryHymnal =
    singlePrimaryHymnalId === undefined ? undefined : hymnals.find((hymnal) => hymnal.id === singlePrimaryHymnalId);
  let hymns = orgHymnal.songs
    .map((song) => getHymnFromSlug({ songSlug: song.slug, hymnals, customMusic }))
    .filter((hymn) => !!hymn) as Hymn[];
  if (singlePrimaryHymnal) {
    // hymns name the canonical hymnal even when the primary hymnal is one of its alternate titles
    const primaryHymnalName = getCanonicalHymnal(hymnals, singlePrimaryHymnal).name;
    const hymnsNotInPrimaryHymnal = hymns.filter((hymn) => hymn.hymnal !== primaryHymnalName);
    hymns = [...hymnsNotInPrimaryHymnal, ...singlePrimaryHymnal.hymns].map((hymn) => ({ ...hymn })); // new object to distinguish from hymn original hymnal
  }

  // sort custom songs to top, ordered alphabetically, non-custom songs to bottom, ordered by song number
  hymns.sort((lhs, rhs) => {
    if (lhs.basePath) {
      if (rhs.basePath) {
        return naturalStringCompare(lhs.title, rhs.title);
      }
      return -1;
    }
    if (rhs.basePath) {
      return 1;
    }
    return naturalStringCompare(lhs.songNumber, rhs.songNumber);
  });

  return {
    id: PrimaryOrgHymnalId,
    name: PrimaryOrgHymnalName,
    slug: PrimaryOrgHymnalSlug,
    icon: getOrgHymnalIcon(org, hymnals, hymns),
    hymns,
  };
}

type OrgHymnalStats = Map<HymnalId, number>; // count of songs per hymnal that are referenced by the org hymnal

function getOrgHymnalIcon(org: ClientOrg, hymnals: HymnalWithHymns[], orgHymns: Hymn[]): HymnalIcon | undefined {
  const stats = getOrgHymnalStats(hymnals, orgHymns);
  for (const [hymnalId, count] of stats.entries()) {
    if ((count / orgHymns.length) * 100 >= OrgHymnalIconThresholdPercentage) {
      const hymnal = hymnals.find(({ id }) => id === hymnalId);
      // the icon of the title the org selected, e.g. an alternate title's cover (issue #3454)
      return hymnal && getDisplayHymnal({ hymnals, hymnal, org, titleHints: [] }).icon;
    }
  }
  return { path: orgHymnalDefaultIconPath };
}

function getOrgHymnalStats(hymnals: HymnalWithHymns[], orgHymns: Hymn[]): OrgHymnalStats {
  // count of songs per hymnal that are referenced by the org hymnal
  const stats = new Map<HymnalId, number>();
  for (const hymn of orgHymns) {
    const hymnal = hymnals.find(({ name }) => name === hymn.hymnal);
    if (!hymnal) {
      continue;
    }
    const id = Number(hymnal.id);
    const count = stats.get(id) ?? 0;
    stats.set(id, count + 1);
  }
  return stats;
}

export function getHymnalsFromHymnalManifests(
  hymnalsManifest: HymnalsManifest,
  hymnalManifests: HymnalManifest[],
): HymnalWithHymns[] {
  const hymnals: HymnalWithHymns[] = hymnalManifests.map((hymnalManifest, index) => {
    const [name, entry] = Object.entries(hymnalsManifest)[index]!;
    return {
      name,
      icon: { path: getHymnalIconPath(name), ...(HymnalsWithIconBorder.includes(name) && { hasBorder: true }) },
      ...entry,
      hymns: getHymnsFromHymnalManifest({
        hymnalName: name,
        hymnalSlug: entry.slug,
        hymnalManifest,
      }),
    };
  });
  // the canonical hymnal precedes its alternate titles, so slug and name lookups find it first
  return hymnals.flatMap((hymnal) => [hymnal, ...getAlternateTitleHymnals(hymnal)]);
}

// A Library entry per alternate title, sharing the canonical hymnal's hymns (and so its song
// slugs) and visibility flags, with its own id, name, icon and publisher (issue #3454)
export function getAlternateTitleHymnals(hymnal: HymnalWithHymns): HymnalWithHymns[] {
  const { alternateTitles, ...canonical } = hymnal;
  return (alternateTitles ?? []).map(({ id, name, slug, icon, publisher }) => ({
    ...canonical,
    id,
    name,
    slug,
    publisher: publisher ?? canonical.publisher,
    icon: { path: getHymnalAlternateTitleIconPath(canonical.name, icon) },
    aliasOf: canonical.slug,
  }));
}

// The title to display for a hymn's hymnal: the one the org selected, else the title whose
// Library tile the playlist was opened from, else the one being browsed, else the canonical
// title the hymn names (issue #3454)
export function useDisplayHymnal(hymn: Pick<Hymn, 'hymnal'> | undefined): HymnalWithHymns | undefined {
  const hymnals = use(HymnalsContext);
  const { currentOrg } = useCurrentOrg();
  // a playlist opened from a hymnal tile is named after that title; the browsed hymnal name is
  // cleared when the household leaves the hymnal, so it is only the second hint
  const [playlist] = useLocalStorage(LocalStorageKey.Playlist);
  const [currentHymnalName] = useLocalStorage(LocalStorageKey.CurrentHymnalName);
  return useMemo(() => {
    const hymnal = hymn && hymnals.find(({ name }) => name === hymn.hymnal);
    return (
      hymnal && getDisplayHymnal({ hymnals, hymnal, org: currentOrg, titleHints: [playlist?.name, currentHymnalName] })
    );
  }, [currentHymnalName, currentOrg, hymn, hymnals, playlist?.name]);
}

export function getHymnsFromHymnalManifest({
  hymnalName,
  hymnalSlug,
  orgId,
  hymnalManifest,
  basePath,
}: {
  hymnalName: string;
  hymnalSlug?: HymnalSlug;
  orgId?: OrgId;
  hymnalManifest: HymnalManifest;
  basePath?: string;
}): Hymn[] {
  return Object.entries(hymnalManifest)
    .map(([songNumber, data]) => ({
      ...data,
      slug: getSongSlug({ songNumber, hymnalSlug, orgId }),
      songNumber,
      hymnal: hymnalName,
      ...(basePath && { basePath }),
    }))
    .sort((lhs, rhs) => naturalStringCompare(lhs.songNumber, rhs.songNumber));
}

export async function refreshOrgHymnal({
  serverHash,
  orgId,
  addDownload,
  customMusic,
  customMusicLoadedOrgIds,
  hymnals,
  downloadsComplete,
}: {
  serverHash?: string;
  orgId?: number;
  addDownload: DownloadFunction;
  customMusic: CustomMusic;
  customMusicLoadedOrgIds: ReadonlySet<OrgId>;
  hymnals: HymnalWithHymns[];
  downloadsComplete: DownloadsCompleteFunction;
}) {
  if (orgId === undefined) {
    return;
  }
  // The prerequisites come first, before the org hymnal document is fetched: the early walks
  // during app start have neither a controlling service worker nor populated hymnals, and
  // fetching the document only to throw the work away is a request for nothing (issue #3517).
  if (!areOrgHymnalPrerequisitesMet({ orgId, hymnals, customMusicLoadedOrgIds })) {
    return;
  }
  // Cleared only by the tab that set it: another tab may be mid-download, and the key is shared.
  let isSynchronizingIndicatorSet = false;
  try {
    // One tab at a time fetches the document and queues the songs (issue #3517 item C2). A tab
    // that cannot take the lock waits for it and then sees the hash the other tab recorded,
    // instead of the old local-storage reentrancy key making it skip while another tab was still
    // working. The lock covers the fetch and the queueing ONLY: holding it across
    // downloadsComplete() would stall every other tab behind this tab's downloads.
    const { value: isQueued } = await withWebLock(orgHymnalRefreshLockName, async () => {
      const localHash = localStorageGet(LocalStorageKey.OrgHymnalHash);
      if (localHash === serverHash) {
        return false;
      }
      const orgHymnal = await server_api.getOrgHymnal(orgId);
      const previousOrgHymnal = localStorageGet(LocalStorageKey.OrgHymnal);
      if (JSON.stringify(orgHymnal) !== JSON.stringify(previousOrgHymnal)) {
        localStorageSet(LocalStorageKey.OrgHymnal, orgHymnal);
      }
      console.log(`downloading org hymnal (orgId: ${orgId})`);
      // purely the Library's "synchronizing" indicator now - never a reentrancy guard
      localStorageSet(LocalStorageKey.OrgHymnalDownloadHashingHash, serverHash);
      isSynchronizingIndicatorSet = true;
      const unmappedCount = await queueSongsInOrgHymnalForDownload({ orgHymnal, addDownload, customMusic, hymnals });
      if (unmappedCount > 0) {
        // The church's songs are known (the prerequisite above), so these are songs its hymnal
        // lists but that are not published - not a reason to keep re-downloading. Visible in the
        // About page's log; the "Unable to map slug" lines name them.
        console.log(`${unmappedCount} of ${orgHymnal.songs.length} org hymnal songs have no music (orgId: ${orgId})`);
      }
      return true;
    });
    if (!isQueued) {
      return;
    }
    const { pending } = await downloadsComplete();
    if (pending > 0) {
      // the drain left work behind (failures backed off, or another tab owns it), so recording
      // the hash would leave the household's org songs permanently unfetched (issue #3517 B4)
      console.log(`not recording the org hymnal hash: ${pending} download(s) still queued (orgId: ${orgId})`);
      return;
    }
    // the hash is recorded only once the songs are actually downloaded (issue #3517 item B4)
    localStorageSet(LocalStorageKey.OrgHymnalHash, serverHash);
    console.log(`finished downloading org hymnal (orgId: ${orgId})`);
  } finally {
    if (isSynchronizingIndicatorSet) {
      localStorageRemove(LocalStorageKey.OrgHymnalDownloadHashingHash);
    }
  }
}

// A controlling service worker (nothing is cached without one), plus the hymnals and custom music
// the org hymnal's song slugs are resolved through
function areOrgHymnalPrerequisitesMet({
  orgId,
  hymnals,
  customMusicLoadedOrgIds,
}: {
  orgId: OrgId;
  hymnals: HymnalWithHymns[];
  customMusicLoadedOrgIds: ReadonlySet<OrgId>;
}): boolean {
  // navigator.serviceWorker is absent in jsdom (tests) and in insecure contexts
  if (!navigator.serviceWorker?.controller) {
    console.log(`waiting for service worker to load before downloading songs from org hymnal`);
    return false;
  }
  if (hymnals.length <= 1) {
    console.log(`waiting for hymnals before downloading songs from org hymnal`);
    return false;
  }
  // The church's own songs must be known, not merely some church's: a switch to a new church
  // arrives before its custom music does, and running then skipped every one of its songs and
  // recorded the church as downloaded (found on a production phone, issue #3517 follow-up).
  // The refresher runs again the moment the church's music becomes known.
  if (!customMusicLoadedOrgIds.has(orgId)) {
    console.log(`waiting for the church's custom music before downloading songs from org hymnal (orgId: ${orgId})`);
    return false;
  }
  return true;
}

async function queueSongsInOrgHymnalForDownload({
  orgHymnal,
  addDownload,
  customMusic,
  hymnals,
}: {
  orgHymnal: OrgHymnal;
  addDownload: DownloadFunction;
  customMusic: CustomMusic;
  hymnals: HymnalWithHymns[];
}): Promise<number> {
  const hymns = getHymns(orgHymnal, customMusic, hymnals);
  for (const hymn of hymns) {
    await addDownload(getUrlPrefixForHymn(hymn), {
      action: DownloadQueueAction.Download,
      manifestEntry: getManifestEntryFromHymn(hymn),
    });
  }
  return orgHymnal.songs.length - hymns.length;
}

// TODO(hewitt): Would be better if we carried along the original manifest entry in the hymn
export function getManifestEntryFromHymn(hymn: Hymn): SongManifestEntry {
  return {
    title: hymn.title,
    psalm: hymn.psalm,
    // the song's manifest content hash, so a queued entry can tell one version of the song from
    // another (an empty hash defeated the queue's retry bookkeeping - issue #3517)
    hash: hymn.hash ?? '',
    isPdfNewer: hymn.isPdfNewer,
    isXmlEmpty: hymn.isXmlEmpty,
    messageCounts: hymn.messageCounts,
    extensions: hymn.extensions,
  } as SongManifestEntry;
}

function getHymns(hymnal: OrgHymnal, customMusic: CustomMusic, hymnals: HymnalWithHymns[]): Hymn[] {
  return hymnal.songs
    .map((song) => {
      const hymn = getHymnFromSlug({ songSlug: song.slug, hymnals, customMusic });
      if (!hymn) {
        console.log(`Unable to map slug ${song.slug} to a hymn`);
      }
      return hymn;
    })
    .filter((hymn) => hymn !== undefined);
}
